import * as _ from 'lodash'
import valueFilters from '../valueFilters';
import validator from '../validator';
import modelFactory, { IModel } from '../modelFactory';
import contactModel, { IContact } from './contactModel';
import jobNoteModel, { IJobNote } from './jobNoteModel';
import jobPaymentModel, { IJobPayment } from './jobPaymentModel';
import jobSettingModel, { IJobSetting } from './jobSettingModel';
import addressModel, { IAddress } from './addressModel';
import statusLogged, { IStatusLogged } from './statusLogged';
import model from './model';
import enums from '../enum';
import { IModelSet } from 'Scripts/modelSet';
import { IContractor } from './contractorModel';
import jobIssueFactory, { IJobIssue } from './jobIssueModel';

export interface IJob extends IModel, IStatusLogged {
	addresses?: IModelSet<IAddress>;
	businessSegment: number;
	builderKey?: string;
	builder?: IContractor;
	builderJobNumber?: string;
	coordinatorKey?: string;
	/**
	 * This will always return IModelSet<IContact> but can be set with IContact[]
	 */
	clientContacts?: IModelSet<IContact> | IContact[];
	accountManagerKey?: string;
	estimatorKey?: string;
	fieldSupervisorKey?: string;
	isListShape?: boolean;
	processorKey?: string;
	payments?: IModelSet<IJobPayment>;
	jobCostKeys: string[];
	siteVisitKeys: string[];
	notes?: IModelSet<IJobNote>;
	jobIssues?: IModelSet<IJobIssue>;
	requiresInitialization?: boolean;
	isRenovation?: boolean;
	settings: IModelSet<IJobSetting>;
	readonly estimateDate?: Date | string;
	primaryClientContact?: IContact;
	readonly primaryClientContactName?: string;
	secondaryClientContact?: IContact;
	readonly secondaryClientContactName?: string;
	readonly leadContactName?: string;
	workSiteAddress?: IAddress;
	billingAddress?: IAddress;
	readonly clientNames?: string;
	readonly contactNames?: string;
	builtAs?: string;
	leadSource?: string;
	referralFrom?: string;
	getSystemGeneratedPayments(jobCostOrchangeOrderKey: string): IJobPayment[];
	getSetting(group: number, name: string, autoCreate?: boolean): IJobSetting;
	setSetting(group: number, name: string, value?: string): IJobSetting;
	getSettingValue(group: number, name: string, defaultValue?: string): string;
	getSettings(group: number);
	getAppliedPaymentTotal(jobCostOrChangeOrderKey: string): number;
	getAppliedPayments(jobCostOrChangeOrderKey: string): IJobPayment[];
	getExternalFormContacts(): IContact[];
}

const jobFactory = modelFactory.createFactory<IJob>('Job', _.assign({}, model, statusLogged('lead'), {
	addresses: {
		setFilters: valueFilters.toModelSet(addressModel),
		rules: validator.validModel(addressModel.rules())
	},
	businessSegment: {
		setFilters: valueFilters.toNumber,
		rules: validator.enum('businessSegments', true) // disallow unknown
	},
	builderKey: {
		setFilters: valueFilters.clean
	},
	builder: {
		nonSerialized: true
	},
	builderJobNumber: {
		setFilters: valueFilters.clean
	},
	coordinatorKey: {
		setFilters: valueFilters.clean
	},
	clientContacts: {
		setFilters: valueFilters.toModelSet(contactModel),
		rules: validator.validModel(contactModel.rules())
	},
	accountManagerKey: {
		setFilters: valueFilters.clean
	},
	estimatorKey: {
		setFilters: valueFilters.clean
	},
	fieldSupervisorKey: {
		setFilters: valueFilters.clean
	},
	processorKey: {
		setFilters: valueFilters.clean
	},
	isListShape: {},
	isRenovation: {
		setFileters: valueFilters.toBoolean
	},
	jobCostKeys: {
		defaultValue: []
	},
	siteVisitKeys: {
		defaultValue: []
	},
	notes: {
		setFilters: valueFilters.toModelSet(jobNoteModel),
		rules: validator.validModel(jobNoteModel.rules())
	},
	payments: {
		setFilters: valueFilters.toModelSet(jobPaymentModel),
		rules: validator.validModel(jobPaymentModel.rules())
	},
	requiresInitialization: {
		setFileters: valueFilters.toBoolean
	},
	settings: {
		setFilters: valueFilters.toModelSet(jobSettingModel),
		rules: validator.validModel(jobSettingModel.rules()),
		defaultValue: []
	},
	jobIssues: {
		setFilters: valueFilters.toModelSet(jobIssueFactory),
		rules: validator.validModel(jobIssueFactory.rules())
	},
	estimateDate: {
		writable: false,
		nonSerialized: true
	},
	_get_estimateDate: function () {
		if (!this.statusLog || this.statusLog.length === 0) { return 0; } // unknown

		const result = _.first(_.sortBy(this.getLogByStatus('estimate'), 'dateCreated'));

		return result ? result.dateCreated : 'N/A';
	},
	primaryClientContact: {
		rules: (v, t) => {
			if (['show-home', 'spec-home', 'design-center'].indexOf(t.builtAs) === -1) {
				return (!v) ? "Primary Client Contact is missing." : undefined;
			}
		},
		nonSerialized: true
	},
	_get_primaryClientContact: function () {
		return this._getContactWithPriority(0);
	},
	_set_primaryClientContact: function (val) {
		this.setClientContactWithPriority(val, 0);
	},
	primaryClientContactName: {
		writable: false,
		nonSerialized: true
	},
	_get_primaryClientContactName: function () {
		return this.primaryClientContact ? this.primaryClientContact.fullName : '';
	},
	secondaryClientContact: {
		nonSerialized: true
	},
	_get_secondaryClientContact: function () {
		return this._getContactWithPriority(1);
	},
	_set_secondaryClientContact: function (val) {
		this.setClientContactWithPriority(val, 1);
	},
	leadContactName: {
		writable: false,
		nonSerialized: true
	},
	_get_leadContactName: function () {
		if (this.primaryClientContactName) {
			return this.primaryClientContactName;
		}
		else {
			const externalFormContacts = this.getExternalFormContacts();

			if (externalFormContacts && externalFormContacts.length) {
				return externalFormContacts[0].fullName;
			}
		}

		return '';
	},
	setClientContactWithPriority: function (contact, priority) {
		if (!this.clientContacts) {
			this.clientContacts = [];
		}

		const existing = _.find(this.clientContacts, c => c.priority === priority && !c.externalFormKey);

		if (existing) {
			if (!contact) {
				this.clientContacts.delete(existing.key);
			}
			else {
				_.assign(existing, contact, { priority: priority });
			}
		}
		else if (contact) {
			contact.priority = priority;
			this.clientContacts[contact.key] = contact;
		}
	},
	_getContactWithPriority: function (priority) {
		return _.find(this.clientContacts, c => c.priority === priority && !c.externalFormKey);
	},
	workSiteAddress: {
		// rules: validator.required(),
		nonSerialized: true
	},
	_get_workSiteAddress: function () {
		const result = this.getAddressByType(enums.addressTypes.Work_Site);

		return result ? result : undefined;
	},
	_set_workSiteAddress: function (val) {
		this.setAddressOfType(val, enums.addressTypes.Work_Site);
	},
	billingAddress: {
		// rules: validator.required(),
		nonSerialized: true
	},
	_get_billingAddress: function () {
		const result = this.getAddressByType(enums.addressTypes.Billing);

		return result ? result : undefined;
	},
	_set_billingAddress: function (val) {
		this.setAddressOfType(val, enums.addressTypes.Billing);
	},
	setAddressOfType: function (address, type) {
		if (!this.addresses) {
			this.addresses = [];
		}

		const existing = _.find(this.addresses, x => { return enums.addressTypes.isSet(type, x.type); });

		if (existing) {
			if (!address) {
				if (existing.type === type) {
					this.addresses.delete(existing.key);
				}
				else {
					existing.type = enums.addressTypes.unset(type, existing.type);
				}
			}
			else {
				_.assign(existing, address, { type: enums.addressTypes.set(type, address.type) });
			}
		}
		else if (address) {
			address.type = enums.addressTypes.set(type, address.type);
			this.addresses[address.key] = address;
		}
	},
	clientNames: {
		writable: false,
		nonSerialized: true
	},
	_get_clientNames: function () {
		return _.map(_.sortBy(_.filter(this.clientContacts, c => !c.externalFormKey && c.priority <= 1), 'priority'), c => c.fullName).join(', ');
	},
	contactNames: {
		writable: false,
		nonSerialized: true
	},
	_get_contactNames: function () {
		return _.map(_.sortBy(_.filter(this.clientContacts, c => !c.externalFormKey && c.priority > 1), 'priority'), c => c.fullName).join(', ');
	},
	getAddressByType: function (type) {
		return _.find(this.addresses, x => {
			return enums.addressTypes.isSet(type, x.type);
		});
	},
	getLogByStatus: function (status) {
		return _.filter(this.statusLog, { status: status });
	},
	getSystemGeneratedPayments: function (jobCostOrChangeOrderKey) {
		return _.filter(this.payments, payment => (payment.jobCostKey === jobCostOrChangeOrderKey || payment.changeOrderKey === jobCostOrChangeOrderKey) && payment.systemGenerated);
	},
	getAppliedPayments: function (jobCostOrChangeOrderKey) {
		return _.filter(this.payments, payment => payment.appliedToContract && (payment.jobCostKey === jobCostOrChangeOrderKey || payment.changeOrderKey === jobCostOrChangeOrderKey));
	},
	getAppliedPaymentTotal: function (jobCostOrChangeOrderKey) {
		return _.reduce(this.getAppliedPayments(jobCostOrChangeOrderKey), (a, v) => {
			return a + (v.amount || 0);
		}, 0);
	},
	builtAs: {
		nonSerialized: true,
		setFilters: valueFilters.clean,
		rules: [(val, target) => {
			if (target.builderKey) {
				const hasValue = validator.hasValue(val);

				if (!hasValue) {
					return 'Built as is required.'
				}
			}
		}]
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the builtAs property
	_get_builtAs: function () {
		const setting = this.getSetting(enums.jobSettingGroups.General, 'builtAs');
		return setting && setting.value;
	},
	_set_builtAs: function (val) {
		this.setSetting(enums.jobSettingGroups.General, 'builtAs', val);
	},
	leadSource: {
		nonSerialized: true,
		setFilters: valueFilters.clean,
		rules: [(val, target) => {
			if (target.businessSegment === enums.businessSegments.Retail) {
				const hasValue = validator.hasValue(val);
				if (!hasValue) {
					return 'Lead source is required.'
				}
			}
		}]
	},
	_get_leadSource: function () {
		const setting = this.getSetting(enums.jobSettingGroups.Marketing, 'leadSource');
		return setting && setting.value;
	},
	_set_leadSource: function (val) {
		this.setSetting(enums.jobSettingGroups.Marketing, 'leadSource', val);
	},
	referralFrom: {
		nonSerialized: true,
		setFilters: valueFilters.toString,
		rules: [(val, target) => {
			if (target.leadSource && target.leadSource.toLowerCase().indexOf('referral') !== -1) {
				const hasValue = validator.hasValue(val);
				if (!hasValue) {
					return 'Referral from is required.'
				}
			}
		}]
	},
	_get_referralFrom: function () {
		const setting = this.getSetting(enums.jobSettingGroups.Marketing, 'referralFrom');
		return setting && setting.value || '';
	},
	_set_referralFrom: function (val) {
		this.setSetting(enums.jobSettingGroups.Marketing, 'referralFrom', val);
	},
	getSetting: function (group, name, autoCreate = false) {
		if (!this.settings) { return; }

		let result = _.find(this.settings, { group: group, name: name });
		if (!result && autoCreate) {
			result = this.settings.add({
				name: name,
				group: group
			});
		}
		return result;
	},
	setSetting: function (group, name, value) {
		if (!this.settings) { return; }

		const isUndefined = typeof value === 'undefined' || value === '';
		let result = this.getSetting(group, name, !isUndefined);

		if (result) {
			if (isUndefined) {
				this.settings.delete(result.key);
				result = undefined;
			}
			else {
				result.value = value;
			}
		}

		return result;
	},
	getSettingValue: function (group, name, defaultValue = '') {
		const setting = this.getSetting(group, name);

		return setting && setting.value || defaultValue;
	},
	getSettings: function (group) {
		return _.filter(this.settings, { group: group });
	},
	getExternalFormContacts: function () {
		return _.orderBy(_.filter(this.clientContacts, c => !!c.externalFormKey), ['dateCreated'], ['desc']);
	},
	clone: function () {
		throw new Error('Jobs cannot be cloned.');
	}
}));

export default jobFactory;
